import { useRef, useCallback } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useAnalysisStore } from '../../../store/useAnalysisStore';
import { useUserStore } from '../../../store/useUserStore';
import { kidsApi } from '../../../assets/api/apis';
import Url from '../../../data/url';
import {
  ANALYSIS_STATUS,
  VIDEO_RECORD_TYPE,
  ON_KIDS_ANALYSIS_THUMBNAIL,
  ON_KIDS_ANALYSIS_TYPE,
} from '../../../data/define';
import FullHeightContainer from '../../../component/common/FullHeightContainer';
import { useAiJobPolling } from '../../../hook/useAiJobPolling';
import AnalysisPendingView from './views/AnalysisPendingView';
import AnalysisSuccessView from './views/AnalysisSuccessView';
import AnalysisErrorView from './views/AnalysisErrorView';
import { useTranslation } from '../../../hook/useTranslation';

export interface AnalysisLocationState {
  type?: number;
  kidId?: number;
  mi?: number[];
  thumbnail?: string;
  reportId?: number;
  replaced?: boolean;
}

export interface SecondAnalysisItem {
  intelligence: number;
  score: number;
  percentile: number;
}

export default function AnalysisResult() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const state = location.state as AnalysisLocationState | null;

  const { status, data, info } = useAnalysisStore((s) => s.analysisResult);
  const setAnalysisResult = useAnalysisStore((s) => s.setAnalysisResult);
  const savedThumbnail = useAnalysisStore((s) => s.savedThumbnail);
  const aiJobIdFromStore = useAnalysisStore((s) => s.aiJobId);
  const kids = useUserStore((s) => s.kids);
  const mainKid = useUserStore((s) => s.mainKid);
  const setUser = useUserStore((s) => s.setUser);

  const imgRef = useRef<HTMLImageElement>(null);

  const jobIdParam = searchParams.get('jobId');
  // 새 분석 요청 중(PENDING 인데 아직 잡 id 없음)에는 URL 의 이전 jobId 로 폴링하지 않는다
  const jobId =
    aiJobIdFromStore ||
    (jobIdParam && status !== ANALYSIS_STATUS.PENDING ? Number(jobIdParam) : null);

  useAiJobPolling(jobId, state);

  const analysisType = (() => {
    const infoTypeNum = info?.type !== undefined ? Number(info?.type) : NaN;
    const stateTypeNum = state?.type !== undefined ? Number(state?.type) : NaN;
    if (!isNaN(infoTypeNum)) return infoTypeNum;
    if (!isNaN(stateTypeNum)) return stateTypeNum;
    return VIDEO_RECORD_TYPE.DAILY;
  })();

  const isSuccess = status === ANALYSIS_STATUS.SUCCESS;
  const reportId = data?.reportId;
  const mediaId = data?.mediaId;

  const { data: reportDetail } = useQuery({
    queryKey: ['reportDetail', analysisType, reportId, mediaId],
    queryFn: async () => {
      if (analysisType === VIDEO_RECORD_TYPE.DAILY) {
        const res = await kidsApi.getKidReportDetail(reportId!);
        return res.data;
      } else {
        const res = await kidsApi.getQuestionVideoDetail(mediaId!);
        return res.data.intelligences[0];
      }
    },
    enabled: isSuccess && (reportId !== undefined || mediaId !== undefined),
    meta: { errorMessage: '리포트 상세 조회에 실패했습니다. 다시 시도해주세요.' },
  });

  const bestScore: SecondAnalysisItem | null =
    info?.mi !== undefined && info?.mi?.length >= 1
      ? (reportDetail?.SecondAnalysis?.find(
          (i: SecondAnalysisItem) => i.intelligence === info!.mi![0]
        ) ?? null)
      : (reportDetail?.SecondAnalysis?.reduce(
          (acc: SecondAnalysisItem | null, curr: SecondAnalysisItem) => {
            if (!acc || curr.percentile < acc.percentile) return curr;
            return acc;
          },
          null
        ) ?? null);

  const resetAndNavigate = useCallback(
    (to: string) => {
      sessionStorage.removeItem(ON_KIDS_ANALYSIS_THUMBNAIL);
      localStorage.removeItem(ON_KIDS_ANALYSIS_TYPE);
      setAnalysisResult({
        status: ANALYSIS_STATUS.STANDBY,
        data: null,
        error: null,
        info: null,
      });
      navigate(to, { replace: true });
    },
    [navigate, setAnalysisResult]
  );

  // 리포트 화면은 선택한 아이(mainKid) 기준이다. 분석 중에 다른 아이로 바꿨더라도
  // 방금 분석한 아이의 리포트를 보여 주도록 그 아이로 맞춘 뒤 이동한다(user-178)
  const goReport = useCallback(() => {
    const analyzedKidId = info?.kidId ?? state?.kidId;
    const analyzedKid =
      analyzedKidId !== undefined && analyzedKidId !== null
        ? kids.find((kid) => kid.id === Number(analyzedKidId))
        : undefined;
    if (analyzedKid && analyzedKid.id !== mainKid?.id) {
      setUser((prev) => ({ ...prev, mainKid: analyzedKid }));
    }
    resetAndNavigate(Url.Report);
  }, [info, state, kids, mainKid, setUser, resetAndNavigate]);

  return (
    <FullHeightContainer>
      <div className="pageInner flex flex-col items-center justify-end" style={{ padding: '0' }}>
        {status === ANALYSIS_STATUS.STANDBY && (
          <div className="w-full h-full flex flex-col gap-3 items-center justify-center">
            <span className="font-bold text-[20px]">{t('analysis.standby_title')}</span>
            <Button variant="outlined" onClick={() => navigate(Url.Home)}>
              {t('nav.home')}
            </Button>
          </div>
        )}

        {status === ANALYSIS_STATUS.PENDING && (
          <AnalysisPendingView
            savedThumbnail={savedThumbnail}
            state={state}
            onNavigateHome={() => navigate(Url.Home)}
          />
        )}

        {status === ANALYSIS_STATUS.SUCCESS && (
          <AnalysisSuccessView
            reportDetail={reportDetail}
            analysisType={analysisType}
            bestScore={bestScore}
            imgRef={imgRef}
            state={state}
            savedThumbnail={savedThumbnail}
            onGoReport={goReport}
          />
        )}

        {(status === ANALYSIS_STATUS.NOTHING || status === ANALYSIS_STATUS.ERROR) && (
          <AnalysisErrorView
            status={status}
            state={state}
            savedThumbnail={savedThumbnail}
            onClose={() => resetAndNavigate(Url.Home)}
            onReRegister={() => {
              sessionStorage.removeItem(ON_KIDS_ANALYSIS_THUMBNAIL);
              navigate(`${Url.KidsRegisterUpload}?type=${encodeURIComponent(analysisType)}`, {
                state: {
                  kidId: state?.kidId,
                  mi: state?.mi,
                  thumbnail: state?.thumbnail,
                  reportId: state?.reportId,
                  replaced: true,
                  isCautionOpen: true,
                },
                replace: true,
              });
              setAnalysisResult({
                status: ANALYSIS_STATUS.STANDBY,
                data: null,
                error: null,
                info: null,
              });
            }}
          />
        )}
      </div>
    </FullHeightContainer>
  );
}
